import type { AppointmentRead, AppointmentRole, InvitationOffer } from './endpoints.ts'
import { isCoachingRole } from './roles.ts'
import type { CoachChoice } from './team-coaches.ts'

// The seats as a page draws them, read off the organigramma and the offers it has already: the
// server's rule (`repositories/seat.py`), so that a page offers only what the API will take.

/** A role of the bench: what a seat of a squad is held in. */
export type BenchRole = Exclude<AppointmentRole, 'official'>

export type TeamSeat =
  | { readonly kind: 'held'; readonly holder: AppointmentRead }
  | { readonly kind: 'booked'; readonly offer: InvitationOffer }
  | { readonly kind: 'free' }

function isOpen(row: AppointmentRead): boolean {
  return row.ended_on == null
}

function onTheBenchOf(row: AppointmentRead, teamId: number): boolean {
  return isOpen(row) && isCoachingRole(row.role) && row.team_id === teamId
}

/** Who holds a seat of the squad, in the order the server sent them. */
export function seatHoldersOf(rows: readonly AppointmentRead[], teamId: number): AppointmentRead[] {
  return rows.filter((row) => onTheBenchOf(row, teamId) && row.seat)
}

/** Staff assigned here with management access from an open club role in the same season. */
export function clubAccessStaffOf(
  rows: readonly AppointmentRead[],
  teamId: number,
): AppointmentRead[] {
  const people = new Map<number, AppointmentRead>()
  for (const row of rows) {
    if (
      onTheBenchOf(row, teamId) &&
      rows.some(
        (office) =>
          isOpen(office) &&
          office.person_id === row.person_id &&
          office.season_year === row.season_year &&
          office.role === 'official',
      )
    ) {
      people.set(row.person_id, row)
    }
  }
  return [...people.values()]
}

function addressOf(email: string): string {
  return email.trim().toLowerCase()
}

/** Whether the offer is for this holder, as the server tells (`is_the_one_named`): by the person when
 *  staff named one, and only then; by the address otherwise. */
export function isOfferedTo(
  offer: Pick<InvitationOffer, 'email' | 'person_id'>,
  holder: Pick<AppointmentRead, 'person_id' | 'email'>,
): boolean {
  if (offer.person_id != null) return offer.person_id === holder.person_id
  return holder.email != null && addressOf(holder.email) === addressOf(offer.email)
}

/** Whether an offer is waiting for this holder to take it: the one who has no account yet and is
 *  invited already, who is not offered «Invita a entrare» again. */
export function hasPendingOffer(
  offers: readonly InvitationOffer[],
  holder: Pick<AppointmentRead, 'person_id' | 'email'>,
): boolean {
  return offers.some((offer) => offer.state === 'pending' && isOfferedTo(offer, holder))
}

/** A live offer books a seat, unless it goes to someone who holds one already: that one gives an
 *  account to an incarico, not a second seat (`book_a_team_seat`, MR !376 decision 2). */
function bookingOffers(
  offers: readonly InvitationOffer[],
  holders: readonly AppointmentRead[],
  books: (offer: InvitationOffer) => boolean,
): InvitationOffer[] {
  return offers.filter(
    (offer) =>
      offer.state === 'pending' &&
      books(offer) &&
      !holders.some((holder) => isOfferedTo(offer, holder)),
  )
}

/** The squad's seats, as many as `limit` (`TeamRead.seat_limit`): held, booked by an offer, or free.
 *  More than `limit` only if the data already holds more, which a page shows rather than hides. */
export function teamSeatsOf(
  rows: readonly AppointmentRead[],
  offers: readonly InvitationOffer[],
  teamId: number,
  limit: number,
): TeamSeat[] {
  const holders = seatHoldersOf(rows, teamId)
  const booked = bookingOffers(
    offers,
    holders,
    (offer) => isCoachingRole(offer.role) && offer.team?.id === teamId,
  )
  const taken: TeamSeat[] = [
    ...holders.map((holder) => ({ kind: 'held', holder }) as const),
    ...booked.map((offer) => ({ kind: 'booked', offer }) as const),
  ]
  const free = Array.from({ length: Math.max(0, limit - taken.length) }, () => ({
    kind: 'free' as const,
  }))
  return [...taken, ...free]
}

export function freeSeatsOf(seats: readonly TeamSeat[]): number {
  return seats.filter((seat) => seat.kind === 'free').length
}

function roleOfSeat(seat: TeamSeat): AppointmentRole | null {
  if (seat.kind === 'held') return seat.holder.role
  if (seat.kind === 'booked') return seat.offer.role
  return null
}

/** The role a free seat proposes first: the one the seats lack, an allenatore before a dirigente
 *  di squadra. «One and one» is what the interface proposes, never a rule (spec #366). */
export function missingBenchRole(seats: readonly TeamSeat[]): BenchRole {
  const held = seats.map(roleOfSeat)
  if (!held.includes('coach')) return 'coach'
  if (!held.includes('team_official')) return 'team_official'
  return 'coach'
}

/** The società's seats taken: its dirigenti di società, and the offers that book one. */
export function clubSeatsTaken(
  rows: readonly AppointmentRead[],
  offers: readonly InvitationOffer[],
): number {
  const officials = rows.filter((row) => isOpen(row) && row.role === 'official')
  return (
    officials.length + bookingOffers(offers, officials, (one) => one.role === 'official').length
  )
}

function choiceOf(row: AppointmentRead): CoachChoice {
  return { person_id: row.person_id, last_name: row.last_name, first_name: row.first_name }
}

/** The dirigenti di società of the organigramma, by person: they stay out of a squad's seats (#340, 8). */
function officialsOf(rows: readonly AppointmentRead[]): ReadonlySet<number> {
  return new Set(
    rows.filter((row) => isOpen(row) && row.role === 'official').map((row) => row.person_id),
  )
}

/** The people of the organigramma off this squad, each once: whom the squad's staff can take.
 *  With `seated`, not a dirigente di società, who stays out of a squad's seats (#340). */
export function staffOffTheSquad(
  rows: readonly AppointmentRead[],
  teamId: number,
  { seated = false }: { readonly seated?: boolean } = {},
): CoachChoice[] {
  const open = rows.filter(isOpen)
  const officials = officialsOf(rows)
  const excluded = new Set(
    open
      .filter((row) => row.team_id === teamId || (seated && officials.has(row.person_id)))
      .map((row) => row.person_id),
  )
  const choices = new Map<number, CoachChoice>()
  for (const row of open) {
    if (!excluded.has(row.person_id)) choices.set(row.person_id, choiceOf(row))
  }
  return [...choices.values()]
}
